import { makeStyles } from '@material-ui/core/styles';

const useLoginStyles = makeStyles(() => ({
  '@keyframes radarSweep': {
    '0%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
    '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' },
  },
  '@keyframes pulse': {
    '0%, 100%': { transform: 'scale(1)', opacity: 0.7 },
    '50%': { transform: 'scale(1.05)', opacity: 1 },
  },
  '@keyframes gridScroll': {
    '0%': {
      backgroundPosition: '0 0, 0 0',
    },
    '100%': {
      backgroundPosition: '0 40px, 0 40px',
    },
  },
  '@keyframes glowPulse': {
    '0%, 100%': { opacity: 0.4 },
    '50%': { opacity: 0.8 },
  },
  '@keyframes fadeInUp': {
    '0%': { opacity: 0, transform: 'translateY(20px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
  '@keyframes scanLine': {
    '0%': { transform: 'translateY(-100%)' },
    '100%': { transform: 'translateY(100vh)' },
  },
  '@keyframes dotFlashing': {
    '0%': { backgroundColor: '#475569' },
    '50%': { backgroundColor: '#CBD5E1' },
    '100%': { backgroundColor: '#F8FAFC' },
  },
  Login: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    width: '100%',
    position: 'fixed',
    top: 0,
    left: 0,
    background: 'linear-gradient(135deg, #020617 0%, #0B1120 50%, #0F1729 100%)',
    overflow: 'hidden',
    color: '#F8FAFC',

    // ========== ANIMATED BACKGROUND ==========

    '&__bg-grid': {
      position: 'absolute',
      bottom: 0,
      left: '-50%',
      right: '-50%',
      height: '70%',
      backgroundImage:
        'linear-gradient(rgba(14, 165, 233, 0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(14, 165, 233, 0.06) 1px, transparent 1px)',
      backgroundSize: '40px 40px',
      transform: 'perspective(400px) rotateX(55deg)',
      transformOrigin: 'bottom center',
      WebkitMaskImage:
        'linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 65%)',
      maskImage:
        'linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 65%)',
      animation: '$gridScroll 8s linear infinite',
      pointerEvents: 'none',
      zIndex: 0,
    },

    '&__bg-radar': {
      position: 'absolute',
      width: '120vmax',
      height: '120vmax',
      borderRadius: '50%',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background:
        'conic-gradient(from 0deg, transparent 0deg, rgba(14, 165, 233, 0.15) 20deg, transparent 50deg)',
      animation: '$radarSweep 8s linear infinite',
      opacity: 0.7,
      pointerEvents: 'none',
      zIndex: 0,
    },

    '&__bg-ring': {
      position: 'absolute',
      borderRadius: '50%',
      border: '1px solid rgba(14, 165, 233, 0.07)',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none',
      zIndex: 0,
      '&--1': {
        width: '15vmax',
        height: '15vmax',
      },
      '&--2': {
        width: '30vmax',
        height: '30vmax',
      },
      '&--3': {
        width: '45vmax',
        height: '45vmax',
      },
      '&--4': {
        width: '60vmax',
        height: '60vmax',
      },
      '&--5': {
        width: '75vmax',
        height: '75vmax',
      },
      '&--6': {
        width: '90vmax',
        height: '90vmax',
      },
      '&--7': {
        width: '105vmax',
        height: '105vmax',
      },
    },

    '&__bg-scan': {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '1px',
      background:
        'linear-gradient(90deg, transparent 0%, rgba(14, 165, 233, 0.25) 50%, transparent 100%)',
      boxShadow: '0 0 15px 2px rgba(14, 165, 233, 0.08)',
      animation: '$scanLine 6s ease-in-out infinite',
      pointerEvents: 'none',
      zIndex: 0,
    },

    // ========== BRANDING ==========

    '&__branding': {
      position: 'relative',
      zIndex: 1,
      textAlign: 'center',
      marginBottom: 40,
      '&__logo': {
        width: '64px',
        height: '64px',
        marginBottom: 20,
        color: '#0EA5E9',
        filter: 'drop-shadow(0 0 20px rgba(14, 165, 233, 0.3))',
        animation: '$pulse 4s ease-in-out infinite',
      },
      '&__title': {
        fontSize: '42px',
        fontWeight: 700,
        color: '#F8FAFC',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        textShadow: '0 0 40px rgba(14, 165, 233, 0.2)',
        marginBottom: 8,
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      },
      '&__subtitle': {
        fontSize: '13px',
        fontWeight: 400,
        color: '#94A3B8',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
      },
      '&__divider': {
        width: 60,
        height: 2,
        background:
          'linear-gradient(90deg, transparent, #0EA5E9, transparent)',
        margin: '24px auto 0',
      },
    },

    // ========== CARD GLOW ==========

    '&__card-glow': {
      position: 'absolute',
      width: '400px',
      height: '400px',
      borderRadius: '50%',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background:
        'radial-gradient(circle, rgba(14, 165, 233, 0.06) 0%, transparent 70%)',
      animation: '$glowPulse 4s ease-in-out infinite',
      pointerEvents: 'none',
      zIndex: 0,
    },

    // ========== LOGIN CARD ==========

    '&__card': {
      position: 'relative',
      zIndex: 2,
      width: '100%',
      maxWidth: 420,
      padding: '40px',
      borderRadius: 16,
      background: 'rgba(15, 23, 42, 0.75)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid rgba(148, 163, 184, 0.12)',
      boxShadow:
        '0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(14, 165, 233, 0.05)',
      animation: '$fadeInUp 0.6s ease-out',

      '&__header': {
        fontSize: '22px',
        fontWeight: 600,
        color: '#F8FAFC',
        marginBottom: 4,
        textAlign: 'center',
      },
      '&__subheader': {
        fontSize: '14px',
        color: '#94A3B8',
        marginBottom: 28,
        textAlign: 'center',
      },

      '&__form': {
        width: '100%',
        '& .MuiTextField-root': {
          marginBottom: 8,
        },
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'rgba(11, 17, 32, 0.5)',
          borderRadius: 8,
          '& fieldset': {
            borderColor: 'rgba(148, 163, 184, 0.2)',
            transition: 'border-color 0.2s ease',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(14, 165, 233, 0.4)',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#0EA5E9',
            borderWidth: 1,
          },
        },
        '& .MuiInputBase-input': {
          color: '#F8FAFC',
          fontSize: '14px',
          padding: '14px 16px',
          '&:-webkit-autofill': {
            WebkitTextFillColor: '#F8FAFC',
            WebkitBoxShadow: '0 0 0 30px rgba(11, 17, 32, 0.95) inset',
            transition: 'background-color 5000s ease-in-out 0s',
          },
        },
        '& label': {
          color: '#CBD5E1 !important',
          fontSize: '14px',
        },
        '& label.Mui-focused': {
          color: '#38BDF8 !important',
        },
        '& .MuiFormHelperText-root': {
          color: '#EF4444',
          fontSize: '12px',
        },
      },

      '&__submit': {
        marginTop: 8,
        padding: '12px',
        width: '100%',
        fontSize: '14px',
        fontWeight: 600,
        borderRadius: 8,
        background: 'linear-gradient(135deg, #0EA5E9 0%, #06B6D4 100%)',
        color: '#FFFFFF',
        border: 'none',
        textTransform: 'none',
        letterSpacing: '0.02em',
        transition: 'box-shadow 0.3s ease, transform 0.15s ease',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(14, 165, 233, 0.4)',
          background: 'linear-gradient(135deg, #0284C7 0%, #0891B2 100%)',
        },
        '&:active': {
          transform: 'scale(0.98)',
        },
      },

      '&__remember': {
        marginTop: 4,
        marginBottom: 4,
        '& .MuiCheckbox-root': {
          color: 'rgba(148, 163, 184, 0.4)',
          padding: '6px 9px',
        },
        '& .Mui-checked': {
          color: '#0EA5E9 !important',
        },
        '& .MuiFormControlLabel-label': {
          color: '#94A3B8',
          fontSize: '13px',
        },
      },

      '&__links': {
        marginTop: 16,
        '& a, & .MuiLink-root': {
          color: '#0EA5E9',
          fontSize: '13px',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          textDecoration: 'none',
          transition: 'color 0.2s ease',
          '&:hover': {
            color: '#38BDF8',
            textDecoration: 'none',
          },
        },
      },
    },

    // ========== LANGUAGE SWITCHER ==========

    '&__lang': {
      position: 'absolute',
      top: 24,
      right: 24,
      zIndex: 10,
      minWidth: 44,
      minHeight: 44,
      borderRadius: 8,
      border: '1px solid rgba(148, 163, 184, 0.2)',
      background: 'rgba(15, 23, 42, 0.6)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      color: '#94A3B8',
      overflow: 'hidden',
      padding: 0,
      transition: 'border-color 0.2s ease, color 0.2s ease',
      '&:hover': {
        borderColor: 'rgba(14, 165, 233, 0.4)',
        color: '#F8FAFC',
        background: 'rgba(15, 23, 42, 0.8)',
      },
      '&__icon': {
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        transition: 'transform 0.25s ease-out',
        fontSize: '20px',
      },
      '&__text': {
        position: 'absolute',
        left: '50%',
        transform: 'translateX(150%)',
        transition: 'transform 0.25s ease-out',
        fontSize: '13px',
        fontWeight: 600,
        letterSpacing: '0.05em',
      },
      '&:hover &__icon': {
        transform: 'translateX(-200%)',
      },
      '&:hover &__text': {
        transform: 'translateX(-50%)',
      },
    },

    // ========== MESSAGES ==========

    '&__message': {
      marginTop: 16,
      borderRadius: 8,
      alignItems: 'center',
      display: 'flex',
      padding: 12,
      '&--border': {
        '&--error': {
          border: '1px solid rgba(239, 68, 68, 0.4)',
          background: 'rgba(239, 68, 68, 0.08)',
          color: '#FCA5A5',
        },
        '&--success': {
          border: '1px solid rgba(16, 185, 129, 0.4)',
          background: 'rgba(16, 185, 129, 0.08)',
          color: '#6EE7B7',
        },
      },
      '&--hidden': {
        opacity: 0,
        height: 0,
        margin: 0,
        padding: 0,
        overflow: 'hidden',
      },
    },

    // ========== COPYRIGHT ==========

    '&__copyright': {
      color: 'rgba(148, 163, 184, 0.4)',
      fontSize: '12px',
      textAlign: 'center',
      letterSpacing: '0.03em',
      position: 'relative',
      zIndex: 1,
    },

    // ========== LOADER (used by PasswordReset) ==========

    '&__loader': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      '&__text': {
        fontSize: '14px',
        color: '#F8FAFC',
      },
      '&__dot-flashing': {
        position: 'relative',
        top: 2,
        left: 15,
        width: '5px',
        height: '5px',
        borderRadius: '100%',
        backgroundColor: '#94A3B8',
        color: '#94A3B8',
        animation: '$dotFlashing 1s infinite linear alternate',
        animationDelay: '.5s',
        '&:before': {
          content: '""',
          display: 'inline-block',
          position: 'absolute',
          top: 0,
          left: '-10px',
          width: '5px',
          height: '5px',
          borderRadius: '50%',
          backgroundColor: '#94A3B8',
          color: '#94A3B8',
          animation: '$dotFlashing 1s infinite alternate',
          animationDelay: '0s',
        },
        '&:after': {
          content: '""',
          display: 'inline-block',
          position: 'absolute',
          top: 0,
          left: '10px',
          width: '5px',
          height: '5px',
          borderRadius: '50%',
          backgroundColor: '#94A3B8',
          color: '#94A3B8',
          animation: '$dotFlashing 1s infinite alternate',
          animationDelay: '1s',
        },
      },
    },

    '&--cursor': {
      cursor: 'pointer',
    },
  },

  // ========== RESPONSIVE ==========

  '@media (max-height: 768px)': {
    Login: {
      '&__branding': {
        marginBottom: 20,
        '&__logo': {
          width: '48px',
          height: '48px',
          marginBottom: 12,
        },
        '&__title': {
          fontSize: '32px',
        },
        '&__divider': {
          margin: '16px auto 0',
        },
      },
      '&__card': {
        padding: '28px',
        maxWidth: 380,
      },
    },
  },
}));

export { useLoginStyles };
